import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductFlagBadgesRefs
 * @property {HTMLElement} badges
 */

/**
 * A custom element wrapping the PDP header badges. Badges are driven by per-market, per-variant
 * "flag" metafields (e.g. `flag_ch_variante.flag_ch_variante_list`), so they must change when the
 * shopper picks a different variant. This element listens for variant update events and swaps in
 * the freshly rendered badges from the response HTML, mirroring `product-title-aroma.js`.
 *
 * @extends {Component<ProductFlagBadgesRefs>}
 */
class ProductFlagBadges extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateBadges);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateBadges);
  }

  /**
   * Replaces the badge markup with the version rendered for the newly selected variant.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updateBadges = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { badges } = this.refs;
    if (!badges) return;

    const newBadges = event.detail.data.html.querySelector(
      `product-flag-badges[data-product-id="${this.dataset.productId}"] [ref="badges"]`
    );
    if (!newBadges) return;

    badges.innerHTML = newBadges.innerHTML;
  };
}

if (!customElements.get('product-flag-badges')) {
  customElements.define('product-flag-badges', ProductFlagBadges);
}
